import { AuthContext, BaseNavLink, SidebarDivider, isDemoModeEnabled } from '@enter/core';
import {
    mdiCheckCircleOutline,
    mdiCheckCircle,
    mdiClipboardTextOutline,
    mdiCogOutline,
    mdiEmailOutline,
    mdiFax,
    mdiPhoneOutline,
    mdiChartLine,
    mdiFileDocumentOutline,
    mdiHandshakeOutline,
    mdiChevronUp,
    mdiChevronDown,
    mdiFlask,
    mdiFingerprint,
    mdiClipboardCheckOutline
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import * as S from './main.sidebar.styles';

type IMainSidebarProps = IInjectedRootStore;

export const MainSidebar: React.FunctionComponent<IMainSidebarProps> = inject('rootStore')(
    observer(
        (props: IMainSidebarProps): React.ReactElement => {
            const { rootStore } = props;
            const { messageStore } = rootStore;
            const { getUnreadTotal, unreadInboxTotal } = messageStore;
            const context = useContext(AuthContext);

            const isMailRoute = useMatch('/mail/*');
            const isFaxRoute = useMatch('/fax/*');
            const isContractsRoute = useMatch('/chargemasters/*');
            const isDocumentsRoute = useMatch('/documents');
            const isPhoneRoute = useMatch('/phone');
            const isFileCabinet = !!isMailRoute || !!isFaxRoute || !!isContractsRoute || !!isDocumentsRoute || !!isPhoneRoute;
            const [clickedFileCabinet, setClickedFileCabinet] = useState<boolean | null>(null);
            const showFileCabinet = clickedFileCabinet !== null ? clickedFileCabinet : isFileCabinet;
            const toggleFileCabinet = () => {
                setClickedFileCabinet((prev) => {
                    const newValue = prev === null ? !isFileCabinet : !prev;
                    return newValue;
                });
            };

            // Reset File Cabinet manual toggle when route changes
            useEffect(() => {
                setClickedFileCabinet(null);
            }, [isMailRoute, isFaxRoute, isContractsRoute, isDocumentsRoute, isPhoneRoute]);

            const isExperimentsRoute = useMatch('/verifications/experiments/*');
            const isEvidenceRoute = useMatch('/verifications/evidence/*');
            const isVerificationsRoute = !!isExperimentsRoute || !!isEvidenceRoute;
            const [clickedVerifications, setClickedVerifications] = useState<boolean | null>(null);
            const showVerifications = clickedVerifications !== null ? clickedVerifications : isVerificationsRoute;
            const toggleVerifications = () => {
                setClickedVerifications((prev) => {
                    const newValue = prev === null ? !isVerificationsRoute : !prev;
                    return newValue;
                });
            };

            // Reset Verifications manual toggle when route changes
            useEffect(() => {
                setClickedVerifications(null);
            }, [isExperimentsRoute, isEvidenceRoute]);

            // NB: setup background poll to check for messages
            useEffect(() => {
                try {
                    context.cancelGroup && context.cancelGroup('messaging');
                    context.setGroup && context.setGroup('messaging');
                    getUnreadTotal();
                    const updateInterval = 3 * 60 * 1000;
                    const interval = setInterval(getUnreadTotal, updateInterval);
                    return (): void => clearInterval(interval);
                } catch (err) {
                    // bury exceptions
                }
            }, [context, getUnreadTotal]);

            const demoModeEnabled = isDemoModeEnabled();

            return (
                <S.SideBar
                    visible={!rootStore.layoutStore.sidebarCollapsed}
                    onClose={() => rootStore.layoutStore.toggleSidebar(true)}>
                    <SidebarDivider />
                    <BaseNavLink to="/claims">
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiClipboardTextOutline} size={1} />
                                Claims
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to="/messaging">
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiCheckCircleOutline} size={1} />
                                Messages{' '}
                                {unreadInboxTotal > 0 && <S.MessageCount>{unreadInboxTotal}</S.MessageCount>}
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to="/reports">
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiChartLine} size={1} />
                                Reports
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <BaseNavLink to="/eligibility">
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiClipboardCheckOutline} size={1} />
                                Eligibility
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    <SidebarDivider />
                    <S.StyledSidebarItem onClick={toggleFileCabinet}>
                        <S.MenuIcon path={mdiFileDocumentOutline} size={1} />
                        <span>File Cabinet</span>
                        <S.CollapseButton 
                            variant="text"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFileCabinet();
                            }}>
                            <S.CollapseIcon path={showFileCabinet ? mdiChevronUp : mdiChevronDown} size={1} open={showFileCabinet} />
                        </S.CollapseButton>
                    </S.StyledSidebarItem>
                    <S.SidebarCollapse in={showFileCabinet} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to="/mail/inbox">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiEmailOutline} size={0.9} />
                                    Mail
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/fax/inbox">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFax} size={0.9} />
                                    Fax
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/chargemasters">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiHandshakeOutline} size={0.9} />
                                    Contracts
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/documents">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFileDocumentOutline} size={0.9} />
                                    Documents
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/phone">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiPhoneOutline} size={0.9} />
                                    Phone
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <S.StyledSidebarItem onClick={toggleVerifications}>
                        <S.MenuIcon path={mdiCheckCircle} size={1} />
                        <span>Verifications</span>
                        <S.CollapseButton 
                            variant="text"
                            onClick={(e): void => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleVerifications();
                            }}>
                            <S.CollapseIcon path={showVerifications ? mdiChevronUp : mdiChevronDown} size={1} open={showVerifications} />
                        </S.CollapseButton>
                    </S.StyledSidebarItem>
                    <S.SidebarCollapse in={showVerifications} timeout="auto" unmountOnExit={false}>
                        <BaseNavLink to="/verifications/experiments">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFlask} size={0.9} />
                                    Experiments
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                        <BaseNavLink to="/verifications/evidence">
                            {(linkProps): React.ReactElement => (
                                <S.CollapseSidebarItem active={linkProps.active}>
                                    <S.CollapseIndent />
                                    <S.SubMenuIcon path={mdiFingerprint} size={0.9} />
                                    Evidence
                                </S.CollapseSidebarItem>
                            )}
                        </BaseNavLink>
                    </S.SidebarCollapse>
                    <SidebarDivider />
                    <BaseNavLink to="/settings">
                        {(linkProps): React.ReactElement => (
                            <S.StyledSidebarItem active={linkProps.active}>
                                <S.MenuIcon path={mdiCogOutline} size={1} />
                                Settings
                            </S.StyledSidebarItem>
                        )}
                    </BaseNavLink>
                    {demoModeEnabled && (
                        <>
                            <S.Spacer />
                            <S.DemoModeIndicator>
                                <S.DemoModeText>Demo Mode</S.DemoModeText>
                            </S.DemoModeIndicator>
                        </>
                    )}
                </S.SideBar>
            );
        },
    ),
);
